import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, Inject, OnDestroy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { SnShare, SnContentType, SnCamera, SnCameraGrid, SnLocation, SnTag } from '@lib/api/entities';
import { SnFormFieldConfig } from '@lib/form';
import { t } from '@lib/translation';
import { SN_DRAWER_DATA, SnDrawerRef } from '@lib/ui/drawer';

import { ISnIsSharable } from '../../interfaces';
import { SnShareFieldsGeneratorService } from '../../services/share-fields-generator.service';

export interface IShareInviteDrawerV2Data {
  object: ISnIsSharable;
  createHandler: (objectId: string, formData: any) => Observable<SnShare | SnShare[]>;
}

@Component({
  selector: 'fs-add-share-drawer-v2',
  templateUrl: './share-invite-drawer-v2.container.html',
  styleUrls: ['./share-invite-drawer-v2.container.scss'],
})
export class ShareInviteDrawerV2Container implements OnDestroy {
  public readonly form = new UntypedFormGroup({});
  public readonly formModel = {};

  public readonly additionalFields: SnFormFieldConfig[];

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(SN_DRAWER_DATA) public readonly data: IShareInviteDrawerV2Data,
    private readonly _drawerRef: SnDrawerRef,
    shareFieldsGenerator: SnShareFieldsGeneratorService,
  ) {
    this.additionalFields = shareFieldsGenerator.getAdditionalFields(data.object.contentType);
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get headerTitle(): string {
    return `${t('common.share')} ${this.contentTypeDisplay}`;
  }

  // SnContentType.Grid value is 'CameraView' but we want to display 'Grid'
  get contentTypeDisplay(): string {
    return this.data.object.contentType === SnContentType.Grid
      ? t('common.grid')
      : this.data.object.contentType;
  }

  get subheaderText(): string {
    switch (this.data.object.contentType) {
      case SnContentType.Camera: {
        const camera = this.data.object as SnCamera;
        return camera.location?.name
          ? `${camera.name} • ${camera.location.name}`
          : camera.name;
      }
      
      case SnContentType.Grid: {
        const grid = this.data.object as SnCameraGrid;
        return grid.name;
      }
      
      case SnContentType.Location: {
        const location = this.data.object as SnLocation;
        const address = location.address;
        return address ? `${location.name} • ${address}` : location.name;
      }
      
      case SnContentType.Tag: {
        const tag = this.data.object as SnTag;
        return tag.name;
      }
      
      default:
        return '';
    }
  }

  public onSubmit(formData: any[]): void {
    this.data
      .createHandler(this.data.object.id, formData)
      .pipe(takeUntil(this._destroy$))
      .subscribe((shares) => {
        this._drawerRef.close(shares);
      });
  }
}
